import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import {
  i18next,
  view,
  site,
  blocks,
  textFormatting,
  contentEditable,
  integration,
  bowser,
} from '@yola/ws-sdk';
import { constants } from '@yola/subscription-manager-js';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import segment from '../../../../analytics/segment';
import ai from '../../../../ai';
import upsell from '../../../../upsell';
import getParentBlockByElement from '../../../../blocks/helpers/get-parent-block-by-element';
import helpers from '../helpers';
import useSelectionSimulation from '../hooks/use-selection-simulation';
import useTextAssistantStatusChange from '../hooks/use-text-assistant-status-change';
import useUndoRedoPrevention from '../hooks/use-undo-redo-prevention';
import useControlTopPosition from '../hooks/use-control-top-position';
import AiTextAssistantContainer from './ai-text-assistant-container';
import prepareSelectedContent from '../helpers/prepare-selected-content';

const {
  helpers: { initiateTextReplacement },
  constants: {
    textAssistantInstructions,
    textAssistantInstructionSuggestions,
    textAssistantInstructionTones,
    textAssistantInstructionTranslationLanguages,
    textAssistantInstructionTypes,
  },
} = ai;
const {
  constants: { events },
  trackers: { trackEvent },
} = segment;
const {
  insertGeneratedContent,
  setSelectionByNodePath,
  getTextAssistantActionGroupOption,
  getTextAssistantSuggestionGroupOption,
  getTextAssistantToneOptions,
  getTextAssistantTranslationOptions,
  restoreRange,
} = helpers;
const {
  hostingPackageFeatureNames: { AI_TEXT_ASSISTANT_QUOTA },
} = constants;
const QUOTA_EXCEEDED_CODE = 'ai_text_assistant_quota_exceeded';

const getCaptions = (instruction = '') => {
  let noResultsText = '';
  let inputPrefix = '';

  switch (instruction) {
    case textAssistantInstructions.TRANSLATE:
      noResultsText = i18next.t('Set your language and hit Enter to adjust the text');
      inputPrefix = i18next.t('Translate to');
      break;
    case textAssistantInstructions.CHANGE_TONE:
      noResultsText = i18next.t('Set your tone and hit Enter to adjust the text');
      inputPrefix = i18next.t('Change tone to');
      break;
    default:
  }

  return {
    placeholder: i18next.t('Tell AI what to do, e.g. "Write about ..."'),
    loading: i18next.t('AI is writing...'),
    cancel: i18next.t('Discard'),
    noResultsText,
    inputPrefix,
  };
};

const isPredefinedInstruction = (instruction) =>
  [
    ...Object.values(textAssistantInstructions),
    ...Object.values(textAssistantInstructionTones),
    ...Object.values(textAssistantInstructionTranslationLanguages),
  ].includes(instruction);

const isPredefinedSuggestion = (instruction) =>
  Object.values(textAssistantInstructionSuggestions).includes(instruction);

const getUserInstructionType = (instruction) => {
  if (isPredefinedInstruction(instruction)) {
    return textAssistantInstructionTypes.PREDEFINED_PROMPT;
  }

  if (isPredefinedSuggestion(instruction)) {
    return textAssistantInstructionTypes.USER_INPUT_WITH_SUGGESTION;
  }

  return textAssistantInstructionTypes.USER_INPUT;
};

const getAutocompleteOptions = (
  isAiTextAssistantRewriteV2FeatureEnabled = false,
  instruction = ''
) => {
  if (isAiTextAssistantRewriteV2FeatureEnabled) {
    switch (instruction) {
      case textAssistantInstructions.TRANSLATE:
        return getTextAssistantTranslationOptions();
      case textAssistantInstructions.CHANGE_TONE:
        return getTextAssistantToneOptions();
      case textAssistantInstructions.CUSTOM:
        return [getTextAssistantSuggestionGroupOption(isAiTextAssistantRewriteV2FeatureEnabled)];
      default:
        return [];
    }
  }

  return [getTextAssistantActionGroupOption(), getTextAssistantSuggestionGroupOption()];
};

const getRewriteParams = (instruction, selectedAiRewriteOption = '') => {
  switch (selectedAiRewriteOption) {
    case textAssistantInstructions.TRANSLATE:
      return {
        instruction: selectedAiRewriteOption,
        instructionParams: {
          language: instruction,
        },
      };
    case textAssistantInstructions.CHANGE_TONE:
      return {
        instruction: selectedAiRewriteOption,
        instructionParams: {
          tone: instruction,
        },
      };
    default:
      return isPredefinedInstruction(instruction)
        ? { instruction }
        : { userInstruction: instruction };
  }
};

const AiRewriteTextContainer = ({
  elementId = null,
  triggerId,
  getContext = Function.prototype,
  selectedAiRewriteOption,
  hideAiWriter = Function.prototype,
  position,
  focusedElementInfo,
}) => {
  const cachedParams = useRef({});
  const pageId = useSelector(site.accessors.getActivePageId);
  const businessCategory = useSelector(site.selectors.getBusinessCategory);
  const limits = useSelector(integration.selectors.getLimits);
  const [featureFlags] = useFeatureFlags(['ai_text_assistant_rewrite_v2']);
  const { ai_text_assistant_rewrite_v2: isAiTextAssistantRewriteV2FeatureEnabled } = featureFlags;
  const captions = getCaptions(selectedAiRewriteOption?.value);
  const autocompleteOptions = getAutocompleteOptions(
    isAiTextAssistantRewriteV2FeatureEnabled,
    selectedAiRewriteOption?.value
  );
  const abortController = useRef();
  const initialFocusedElementInfo = useRef(focusedElementInfo);
  const currentFocusedElementInfo = useRef(focusedElementInfo);
  const [selectedInstruction, setSelectedInstruction] = useState(null);

  const { clearSelectionImitation, restoreSelectionImitation } = useSelectionSimulation(elementId);

  const { isLoading, onLoading, onSuccess, onError, onCancel } = useTextAssistantStatusChange({
    getContext,
    hideAiWriter,
    shouldHideWriterAfterRequest: isAiTextAssistantRewriteV2FeatureEnabled,
  });
  useUndoRedoPrevention();
  const { topPosition } = useControlTopPosition(elementId);

  useEffect(
    () => () => {
      if (bowser.ios || bowser.iPadOS) {
        restoreSelectionImitation();
      }
    },
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    []
  );

  if (!elementId) return null;

  const handleTextReplacement = async (params) => {
    const element = view.accessors.getLiveElement(elementId);
    const blockElement = getParentBlockByElement(element);
    abortController.current = new AbortController();
    const { signal } = abortController.current;

    onLoading();

    try {
      const response = await initiateTextReplacement(params, abortController.current);
      const { content } = response.results;

      if (signal.aborted) {
        return;
      }

      clearSelectionImitation();

      await insertGeneratedContent(element, content);
      currentFocusedElementInfo.current = contentEditable.helpers.getFocusedElementInfo();

      restoreSelectionImitation();
      onSuccess();
    } catch (err) {
      if (!signal.aborted) {
        const isAiTextAssistantQuotaExceeded =
          err.response?.status === 409 && err.response?.data?.code === QUOTA_EXCEEDED_CODE;

        console.error(err);
        onError(err, isAiTextAssistantQuotaExceeded);

        if (isAiTextAssistantQuotaExceeded) {
          const currentLimit = get(limits, 'aiTextAssistantQuota');
          upsell.operations
            .requestFeatureAvailability(AI_TEXT_ASSISTANT_QUOTA, { ...currentLimit })
            .then(() => {
              handleTextReplacement(params);
            })
            .catch(() => {
              // eslint-disable-next-line no-console
              console.log('Upgrade flow was canceled');
            });
        } else {
          trackEvent(events.AI_TEXT_ASSISTANT_ERROR_CAUGHT, {
            blockId: blocks.accessors.getBlockIdByElement(blockElement),
            blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
            triggerId,
            businessCategory,
            errorReason: err?.results?.code || null,
            errorMessage: err?.results?.msg || null,
          });
        }
      }
    }
  };

  const handleRewrite = (instruction) => {
    clearSelectionImitation();

    const selection = textFormatting.accessors.getAdvancedSelection();
    const contentSelection = prepareSelectedContent(selection);

    restoreSelectionImitation();

    const params = {
      contentSelection,
      pageId,
      ...getRewriteParams(instruction, selectedAiRewriteOption?.value),
    };

    cachedParams.current = params;

    handleTextReplacement(params);
  };

  const handleRetry = () => {
    const element = view.accessors.getLiveElement(elementId);
    const blockElement = getParentBlockByElement(element);

    trackEvent(events.AI_TEXT_ASSISTANT_TRY_AGAIN_TRIGGER_CLICKED, {
      blockId: blocks.accessors.getBlockIdByElement(blockElement),
      blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
      triggerId,
      businessCategory,
    });

    handleTextReplacement(cachedParams.current);
  };

  const handleCancel = () => {
    abortController.current?.abort();
    onCancel();
    clearSelectionImitation();

    if (!bowser.firefox) {
      const element = view.accessors.getLiveElement(elementId);
      setSelectionByNodePath(element, initialFocusedElementInfo.current.selectionData);
    }

    if (bowser.ios || bowser.iPadOS) {
      restoreRange(textFormatting.accessors.getAdvancedSelection(), elementId);
    }
  };

  const handleSubmit = (instruction) => {
    const element = view.accessors.getLiveElement(elementId);
    const blockElement = getParentBlockByElement(element);

    trackEvent(events.AI_TEXT_USER_INPUT_SUBMITTED, {
      blockId: blocks.accessors.getBlockIdByElement(blockElement),
      blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
      triggerId,
      userInstructionType: getUserInstructionType(selectedInstruction || instruction),
      userInput: instruction,
      businessCategory,
    });

    handleRewrite(instruction);
  };

  const handleSelect = (instruction) => {
    setSelectedInstruction(instruction);

    if (isPredefinedSuggestion(instruction)) {
      return {
        withRetry: false,
      };
    }

    handleSubmit(instruction);

    return {
      withRetry: true,
    };
  };

  return (
    <AiTextAssistantContainer
      captions={captions}
      autocompleteOptions={autocompleteOptions}
      isLoading={isLoading}
      onSelect={handleSelect}
      onSubmit={handleSubmit}
      onRetry={handleRetry}
      onCancel={handleCancel}
      position={{ ...position, top: topPosition }}
      {...(isAiTextAssistantRewriteV2FeatureEnabled && { selectedAiRewriteOption })}
    />
  );
};

AiRewriteTextContainer.propTypes = {
  triggerId: PropTypes.string.isRequired,
  elementId: PropTypes.string,
  getContext: PropTypes.func,
  selectedAiRewriteOption: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  hideAiWriter: PropTypes.func,
  position: PropTypes.shape({
    left: PropTypes.number,
    width: PropTypes.number,
  }).isRequired,
  focusedElementInfo: PropTypes.shape({
    selectionData: PropTypes.shape().isRequired,
  }).isRequired,
};

export default AiRewriteTextContainer;
